import {ErrorHandler, inject, LOCALE_ID, NgModule, provideAppInitializer} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {AppRoutingModule} from './app-routing.module';
import {AppComponent} from './app.component';
import {BrowserAnimationsModule} from '@angular/platform-browser/animations';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptors,
  withInterceptorsFromDi
} from '@angular/common/http';
import {TranslateLoader, TranslateModule, TranslateService} from '@ngx-translate/core';
import {registerLocaleData} from '@angular/common';
import localeDe from '@angular/common/locales/de';
import {OAuthModule, OAuthStorage} from 'angular-oauth2-oidc';
import {ComponentsModule} from './components/components.module';
import {GlobalErrorHandler} from './services/error-handling/global-error-handler';
import {NgProgressHttp, progressInterceptor} from 'ngx-progressbar/http';
import {provideLottieOptions} from 'ngx-lottie';
import {TokenInterceptor} from 'ngx-auth-util-lib';
import {provideMatomo, withRouter} from 'ngx-matomo-client';
import {environment} from 'src/environments/local/environment';
import {lottiPlayerFactory} from 'ngx-heka-design-lib';
import {NgProgressbar, provideNgProgressOptions} from 'ngx-progressbar';
import {forkJoin, Observable} from "rxjs";
import {map} from "rxjs/operators";
import deepmerge from "deepmerge";
import {MAT_DIALOG_DEFAULT_OPTIONS} from "@angular/material/dialog";

export class HekaTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) {
  }

  getTranslation(lang: string): Observable<any> {
    const global = this.http.get(`assets/i18n-global/${lang}.json`);
    const local = this.http.get(`assets/i18n/${lang}.json`);

    return forkJoin([global, local]).pipe(
      map(([globalTrans, localTrans]) => {
        // Merge translations (local overwrites global in case of conflict)
        return deepmerge(globalTrans, localTrans);
      })
    );
  }
}

export function createTranslateLoader(http: HttpClient) {
  return new HekaTranslateLoader(http);
}

registerLocaleData(localeDe);

export function appInitializerFactory(translate: TranslateService) {
  return () => {
    translate.setDefaultLang('de');
    return translate.use('de').toPromise();
  };
}

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    OAuthModule.forRoot(),
    TranslateModule.forRoot({
      defaultLanguage: 'de',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [HttpClient]
      }
    }),
    ComponentsModule,
    NgProgressbar,
    NgProgressHttp
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(inject(TranslateService));
      return initializerFn();
    }),
    {
      provide: LOCALE_ID,
      useValue: 'de'
    },
    {provide: ErrorHandler, useClass: GlobalErrorHandler},
    {provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true},
    {provide: OAuthStorage, useValue: localStorage},
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS, useValue: {
        autoFocus: 'dialog',
        delayFocusTrap: true,
        restoreFocus: true
      }
    },
    provideNgProgressOptions({
      flat: false,
      spinner: false
    }),
    provideMatomo({scriptUrl: environment.matomoSrc}, withRouter()),
    provideLottieOptions({player: lottiPlayerFactory}),
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([progressInterceptor])) //withInterceptorsFromDi() needed for class based interceptors
  ]
})
export class AppModule {
}
